import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { fadeUp, stagger } from "../lib/motion";
import { HUGH_ACRONYM } from "../lib/site";
import { Link } from "react-router-dom";

function AcronymLetter({ letter, containerRef }) {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "center center"],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [0.45, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.35, 1], [0, 0.6, 1]);
  return (
    <motion.span
      style={{ scale, opacity, transformOrigin: "left top" }}
      className="inline-block text-8xl lg:text-9xl font-black leading-none text-stroke-lime transition-colors duration-300 group-hover:text-[#C6FF00]"
    >
      {letter}
    </motion.span>
  );
}

export const HughAcronym = () => {
  const acronymRef = useRef(null);
  return (
    <section id="method" className="relative pt-14 pb-24 lg:pt-16 lg:pb-32 bg-[#0A0C0F]" data-testid="hugh-acronym">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="mb-14"
        >
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.3em] text-[#C6FF00] mb-5">
            The H.U.G.H. Method&trade;
          </motion.p>
          <motion.h3
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.9] max-w-4xl"
          >
            A direct path from hidden talent to <span className="text-[#C6FF00]">booked clients.</span>
          </motion.h3>
          <motion.p variants={fadeUp} className="mt-6 max-w-2xl text-[#8A939B] font-medium leading-relaxed">
            We don't just build websites. Every Hugh Launch client goes through our proprietary
            four-phase framework — Highlight, Upgrade, Generate, Harvest — engineered to turn
            what you're best at into consistent income.
          </motion.p>
          <motion.p variants={fadeUp} className="mt-10 text-sm font-bold uppercase tracking-[0.25em] text-white">
            What <span className="text-[#C6FF00]">H.U.G.H.</span> stands for
          </motion.p>
        </motion.div>

        <motion.div
          ref={acronymRef}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px"
        >
          {HUGH_ACRONYM.map((h, i) => (
            <motion.div
              key={h.title}
              variants={{
                hidden: { opacity: 0, y: 48 },
                show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
              }}
              className="group relative overflow-hidden bg-[#0A0C0F] ring-1 ring-white p-8 lg:p-10 min-h-[300px] flex flex-col hover:-translate-y-1.5 transition-transform duration-300"
              data-testid={`hugh-card-${i}`}
            >
              <AcronymLetter letter={h.letter} containerRef={acronymRef} />
              <div className="mt-auto pt-8">
                <h4 className="text-2xl lg:text-3xl font-black uppercase tracking-tight text-white">
                  {h.title}
                </h4>
                <span className="mt-3 block h-0.5 w-8 bg-[#C6FF00] transition-all duration-300 group-hover:w-16" />
                <p className="mt-4 text-[#8A939B] font-medium leading-relaxed">{h.body}</p>
              </div>
              <div className="pointer-events-none absolute -bottom-16 -right-16 h-40 w-40 rounded-full bg-[#C6FF00]/0 blur-3xl transition-all duration-500 group-hover:bg-[#C6FF00]/10" />
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex justify-center"
        >
          <Link
            to="/method"
            data-testid="explore-method-btn"
            className="group flex items-center gap-2 rounded-full border border-[#C6FF00]/60 px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#C6FF00] transition-colors duration-300 hover:bg-[#C6FF00] hover:text-[#0A0C0F]"
          >
            Explore the Full Method
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
